import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowRight, Sparkles } from "lucide-react";
import { BRAND, PRODUCTS } from "../lib/brand";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "./ui/sheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [openMega, setOpenMega] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpenMega(false);
    setMobileOpen(false);
  }, [pathname]);

  const isDigipos = pathname.startsWith("/digipos");

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled || isDigipos || pathname !== "/" ? "bg-[#0B2545] shadow-[0_4px_20px_rgba(0,0,0,0.18)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-[84px] flex items-center justify-between">
        {/* Logo */}
        <Link to="/" data-testid="nav-logo" className="flex items-center group">
          <img
            src={BRAND.logoUrl}
            alt="Gravityinc"
            className="h-12 w-auto object-contain transition group-hover:scale-105"
            style={{
              filter: "saturate(0) brightness(3)",
              mixBlendMode: "screen",
            }}
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8 text-white/85 text-sm font-medium">
          <NavItem to="/" label="Home" />
          <NavItem to="/about" label="About Us" />
          {/* DigiPOS mega */}
          <div
            className="relative"
            onMouseEnter={() => setOpenMega(true)}
            onMouseLeave={() => setOpenMega(false)}
          >
            <button
              data-testid="nav-digipos-trigger"
              className="flex items-center gap-1 hover:text-white transition"
            >
              DigiPOS Softwares<ChevronDown className={`h-4 w-4 transition ${openMega ? "rotate-180" : ""}`} />
            </button>
            {openMega && (
              <div
                data-testid="nav-digipos-mega"
                className="absolute left-1/2 -translate-x-1/2 top-full pt-4"
              >
                <div className="w-[760px] bg-white text-[#1A1A2E] rounded-2xl shadow-[0_24px_60px_rgba(11,37,69,0.22)] border border-[#DDE6F0] grid grid-cols-5 overflow-hidden">
                  <div className="col-span-3 p-6">
                    <div className="text-xs font-mono uppercase tracking-widest text-[#1565C0] mb-3">
                      Our Software Suite
                    </div>
                    <ul className="space-y-1">
                      {PRODUCTS.map((p) => (
                        <li key={p.slug}>
                          <Link
                            to={`/digipos/${p.slug}`}
                            data-testid={`nav-mega-${p.slug}`}
                            className="flex items-start gap-3 p-3 rounded-lg hover:bg-[#F5F8FF] transition group"
                          >
                            <span
                              className="h-9 w-9 rounded-lg grid place-items-center shrink-0"
                              style={{ background: `${p.accent}15`, color: p.accent }}
                            >
                              <Sparkles className="h-4 w-4" />
                            </span>
                            <span>
                              <span className="block font-semibold text-sm">{p.name}</span>
                              <span className="block text-xs text-[#6B7280]">{p.oneLiner}</span>
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="col-span-2 bg-[#F5F8FF] p-6 flex flex-col justify-between border-l border-[#DDE6F0]">
                    <div>
                      <div className="text-xs font-mono uppercase tracking-widest text-[#1565C0] mb-2">
                        Need help?
                      </div>
                      <h4 className="font-display font-semibold text-lg leading-snug mb-2">
                        Not sure which product fits?
                      </h4>
                      <p className="text-sm text-[#6B7280]">
                        Our team can recommend the right DigiPOS solution for your business size & industry.
                      </p>
                    </div>
                    <Link to="/contact" data-testid="nav-mega-cta" className="gi-btn-primary mt-4 w-fit">
                      Talk to an Expert <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
          <NavItem to="/hardware" label="Hardware" />
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-contact-btn"
            className="text-white/95 border border-white/40 hover:border-white hover:bg-white/10 px-5 py-2 rounded-lg text-sm font-medium transition"
          >
            Contact Us
          </Link>
          <Link to="/contact" data-testid="nav-demo-btn" className="gi-btn-primary">
            Request a Demo
          </Link>
        </div>

        {/* Mobile */}
        <div className="lg:hidden">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="nav-mobile-toggle"
                className="text-white p-2"
                aria-label="Open menu"
              >
                {mobileOpen ? <X /> : <Menu />}
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#0B2545] text-white border-none w-[88%]">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <div className="flex flex-col gap-3 pt-8">
                <MobileLink to="/" label="Home" />
                <MobileLink to="/about" label="About Us" />
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="digipos" className="border-white/10">
                    <AccordionTrigger className="text-white hover:no-underline text-base">
                      DigiPOS 
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="flex flex-col gap-1 pl-2">
                        
                        {PRODUCTS.map((p) => (
                          <MobileLink
                            key={p.slug}
                            to={`/digipos/${p.slug}`}
                            label={p.name}
                            sub
                          />
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
                <MobileLink to="/hardware" label="Hardware" />
                <MobileLink to="/contact" label="Contact Us" />
                <Link to="/contact" data-testid="nav-mobile-demo" className="gi-btn-primary mt-4 justify-center">
                  Request a Demo
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

const NavItem = ({ to, label }) => (
  <NavLink
    to={to}
    end={to === "/"}
    data-testid={`nav-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
    className={({ isActive }) =>
      `relative hover:text-white transition ${isActive ? "text-white" : ""}`
    }
  >
    {({ isActive }) => (
      <>
        {label}
        {isActive && (
          <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 h-[2px] w-5 bg-[#00B4D8] rounded-full" />
        )}
      </>
    )}
  </NavLink>
);

const MobileLink = ({ to, label, sub }) => (
  <Link
    to={to}
    data-testid={`nav-mobile-${label.toLowerCase().replace(/\s+/g, "-")}`}
    className={`block py-2 ${sub ? "text-white/70 text-sm" : "text-white text-base font-medium border-b border-white/10"}`}
  >
    {label}
  </Link>
);

export default Navbar;
